import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Briefcase, KeyRound } from "lucide-react";
import { JackstoneLogo } from "@/components/Logo";
import { LoginHero } from "@/components/login/LoginHero";
import { StaffLoginForm } from "@/components/login/StaffLoginForm";
import { EmployeePinPad } from "@/components/login/EmployeePinPad";

export default function Login() {
  const navigate = useNavigate();
  const { login, loginEmployee } = useAuth();
  const [tab, setTab] = useState("staff"); // staff | employee
  // Staff (admin/tester/viewer) form
  const [email, setEmail] = useState("admin@jackstone.io");
  const [password, setPassword] = useState("admin123");
  const [staffError, setStaffError] = useState("");
  const [staffSubmitting, setStaffSubmitting] = useState(false);
  // Employee (PIN pad)
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [empError, setEmpError] = useState("");
  const [empSubmitting, setEmpSubmitting] = useState(false);

  const onStaffSubmit = async (e) => {
    e.preventDefault();
    setStaffError("");
    setStaffSubmitting(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setStaffError(formatApiError(err));
    } finally {
      setStaffSubmitting(false);
    }
  };

  const pressDigit = (d) => {
    if (pin.length >= 6) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 6) {
      // Auto-submit when 6 digits reached.
      setTimeout(() => submitEmployeeRef(next), 80);
    }
  };
  // We need a stable ref so we don't re-submit on every render.
  const submitEmployeeRef = async (fullPin) => {
    if (!username.trim() || fullPin.length !== 6) return;
    setEmpError("");
    setEmpSubmitting(true);
    try {
      await loginEmployee(username.trim(), fullPin);
      navigate("/me");
    } catch (err) {
      setEmpError(formatApiError(err));
      setPin("");
    } finally {
      setEmpSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#f5f1ec]">
      {/* Left image panel */}
      <LoginHero />

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 grid-bg">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <JackstoneLogo className="h-8 w-auto" variant="brand" />
          </div>

          <div className="eyebrow mb-3">Authenticate</div>
          <h2 className="font-display text-5xl md:text-6xl tracking-tight mb-3 text-[#33424f] leading-[0.95]">
            SIGN IN TO
            <br />
            THE WORKSHOP
          </h2>

          {/* Tab switcher */}
          <div className="flex border border-[#33424f]/30 mb-0 bg-white">
            <button
              data-testid="login-tab-staff"
              type="button"
              onClick={() => setTab("staff")}
              className={`flex-1 px-4 py-3 subtitle text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors ${tab === "staff" ? "bg-[#33424f] text-white" : "text-[#33424f] hover:bg-[#f5f1ec]"}`}
            >
              <Briefcase className="w-3.5 h-3.5" /> Office staff
            </button>
            <button
              data-testid="login-tab-employee"
              type="button"
              onClick={() => setTab("employee")}
              className={`flex-1 px-4 py-3 subtitle text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors border-l border-[#33424f]/30 ${tab === "employee" ? "bg-[#33424f] text-white" : "text-[#33424f] hover:bg-[#f5f1ec]"}`}
            >
              <KeyRound className="w-3.5 h-3.5" /> Test person · PIN
            </button>
          </div>

          {tab === "staff" ? (
            <StaffLoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              error={staffError}
              submitting={staffSubmitting}
              onSubmit={onStaffSubmit}
            />
          ) : (
            <EmployeePinPad
              username={username}
              setUsername={setUsername}
              pin={pin}
              setPin={setPin}
              error={empError}
              submitting={empSubmitting}
              pressDigit={pressDigit}
            />
          )}
        </div>
      </div>
    </div>
  );
}
